// web/src/screens/OrderScreen.jsx
// Split-screen: LEFT = live ticket panel, RIGHT = menu grid with category tabs
import { useState, useEffect } from 'react';
import { useNav, useAuth } from '../App';
import { Menu, Tickets, Lines, SyncQueue } from '../db';
import { apiClient } from '../api';
import { iconForCategory } from '../utils/categoryIcons';
import { VENUE_LABELS } from '../utils/venueLabels';

const C = {
  bg: 'var(--bg)', surface: 'var(--surface)', orange: 'var(--orange)',
  text: 'var(--text)', muted: 'var(--text-muted)', danger: 'var(--danger)',
};

export default function OrderScreen() {
  const { params, navigate }        = useNav();
  const { user }                    = useAuth();
  const { table, ticketId }         = params;
  const [ticket, setTicket]         = useState(null);
  const [lines, setLines]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems]   = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [sending, setSending]       = useState(false);
  const [searchText, setSearchText] = useState('');
  const [ticketLoadError, setTicketLoadError] = useState(false);

  useEffect(() => { loadData(); }, [ticketId]);

  const loadData = async () => {
    // Load ticket + lines from API
    try {
      const { data } = await apiClient.get(`/tickets/${ticketId}`);
      setTicket(data);
      setLines(data.lines || []);
      setTicketLoadError(false);
    } catch {
      // Couldn't reach the server — falling back to this device's local
      // cache, which may not reflect lines added from another device/session.
      // Surface that distinction rather than silently treating it as an
      // empty ticket (an empty result here does NOT mean an empty ticket).
      setTicketLoadError(true);
      const local = await Tickets.getById(ticketId);
      setTicket(local);
      const localLines = await Lines.getByTicket(ticketId);
      setLines(localLines);
    }

    // Load menu from API, fall back to Dexie
    try {
      const { data } = await apiClient.get('/menu');
      await Menu.upsertCategories(data.categories);
      await Menu.upsertItems(data.items);
      setCategories(data.categories);
      setMenuItems(data.items);
      if (data.categories.length) setActiveCategory(data.categories[0].id);
    } catch {
      const cats  = await Menu.getCategories();
      const items = await Menu.getItems();
      setCategories(cats);
      setMenuItems(items);
      if (cats.length) setActiveCategory(cats[0].id);
    }
  };

  const addItem = async (item) => {
    // Check if item already in lines
    const existing = lines.find(l => l.menu_item_id === item.id && !l.voided);

    if (existing) {
      // Increment existing line
      const newQty = existing.qty + 1;
      setLines(prev => prev.map(l => l.id === existing.id ? { ...l, qty: newQty } : l));
      try {
        await apiClient.patch(`/tickets/${ticketId}/lines/${existing.id}`, { qty: newQty });
      } catch {
        await SyncQueue.add('update_line', { ticketId, lineId: existing.id, qty: newQty });
      }
      return;
    }

    // New line — optimistic add
    const optimisticLine = {
      id: `local-${Date.now()}`,
      ticket_id: ticketId,
      menu_item_id: item.id,
      name: item.name,
      price: parseFloat(item.price),
      qty: 1,
      voided: false,
    };
    setLines(prev => [...prev, optimisticLine]);

    try {
      const { data: line } = await apiClient.post(`/tickets/${ticketId}/lines`, {
        menu_item_id: item.id,
        qty: 1,
      });
      // Replace optimistic with real server line
      setLines(prev => prev.map(l => l.id === optimisticLine.id ? line : l));
    } catch {
      await SyncQueue.add('add_line', { ticketId, menu_item_id: item.id, qty: 1 });
      await Lines.upsert({ ...optimisticLine, synced: false });
    }
  };

  const updateQty = async (line, delta) => {
    const newQty = line.qty + delta;
    if (newQty <= 0) return voidLine(line);
    setLines(prev => prev.map(l => l.id === line.id ? { ...l, qty: newQty } : l));
    try {
      await apiClient.patch(`/tickets/${ticketId}/lines/${line.id}`, { qty: newQty });
    } catch {
      await SyncQueue.add('update_line', { ticketId, lineId: line.id, qty: newQty });
    }
  };

  const voidLine = async (line) => {
    setLines(prev => prev.filter(l => l.id !== line.id));
    try {
      await apiClient.delete(`/tickets/${ticketId}/lines/${line.id}`);
    } catch {}
  };

  const goBack = async () => {
    // If no lines added, void the ticket and free the table
    const activeLines = lines.filter(l => !l.voided);
    if (activeLines.length === 0 && ticketId) {
      try {
        await apiClient.post(`/tickets/${ticketId}/void`, {});
      } catch (err) {
        // Server refused the void (e.g. 409 — ticket actually has active
        // lines, likely because our local view was stale/offline) or the
        // request otherwise failed. Either way, do NOT force the table to
        // free here — that would desync tables.status from a ticket that's
        // still genuinely open. Just log and let the waiter navigate back;
        // the table keeps showing its real state.
        console.error('Void failed, leaving table status untouched:', err);
      }
    }
    navigate('tables');
  };

  const sendToKitchen = async () => {
    if (!lines.length) return;
    setSending(true);
    try {
      // Pass empty object so Content-Type is set and body is not empty
      await apiClient.post(`/tickets/${ticketId}/send`, {});
      navigate('confirmed', { table, ticketId, lines });
    } catch (err) {
      alert(err.data?.error || 'Failed to send order. Try again.');
    } finally {
      setSending(false);
    }
  };

  const total = lines.reduce((sum, l) => sum + (parseFloat(l.price) * l.qty), 0);
  // Bulk-stock parents (sellable_directly=false) exist only to be drawn down
  // by their portion children — they don't belong in the waiter's own grid.
  const sellableItems = menuItems.filter(i => i.sellable_directly !== false);
  const isSearching = searchText.trim().length > 0;
  const filteredItems = isSearching
    ? sellableItems.filter(i => i.name.toLowerCase().includes(searchText.trim().toLowerCase()))
    : sellableItems.filter(i => i.category_id === activeCategory);
  const categoryNameById = Object.fromEntries(categories.map(c => [c.id, c.name]));
  const showSend = VENUE_LABELS[user?.type]?.showSend ?? true;
  const sendLabel = VENUE_LABELS[user?.type]?.sendLabel ?? '🔥 SEND TO KITCHEN';

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={goBack}>← TABLES</button>
        <span style={s.tableTitle}>{table?.name?.toUpperCase()}</span>
        <button style={s.myOrdersBtn} onClick={() => navigate('myorders')}>MY ORDERS</button>
      </div>

      {ticketLoadError && (
        <div style={s.loadErrorBanner}>
          ⚠️ Couldn't reach server — showing cached order data, may be out of date
        </div>
      )}

      <div className="order-body" style={s.body}>
        {/* LEFT — ticket panel */}
        <div className="order-cart" style={s.ticketPanel}>
          <div style={s.ticketHeader}>
            <span style={s.ticketLabel}>ORDER</span>
            <span style={s.ticketTotal}>KES {total.toLocaleString()}</span>
          </div>

          <div style={s.linesList}>
            {lines.length === 0 && (
              <p style={s.emptyMsg}>Tap items on the right to add</p>
            )}
            {lines.filter(l => !l.voided).map(line => (
              <div key={line.id} style={s.lineItem}>
                <span style={s.lineName}>{line.name}</span>
                <div style={s.lineControls}>
                  <button style={s.qtyBtn} onClick={() => updateQty(line, -1)}>−</button>
                  <span style={s.lineQty}>{line.qty}</span>
                  <button style={s.qtyBtn} onClick={() => updateQty(line, +1)}>+</button>
                </div>
                <span style={s.lineSubtotal}>
                  KES {(parseFloat(line.price) * line.qty).toLocaleString()}
                </span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={s.ticketActions}>
            {showSend && (
              <button
                style={{ ...s.sendBtn, opacity: (!lines.length || sending) ? 0.5 : 1 }}
                onClick={sendToKitchen}
                disabled={!lines.length || sending}
              >
                {sending ? 'SENDING...' : sendLabel}
              </button>
            )}
            <button
              style={{ ...s.billBtn, opacity: !lines.length ? 0.5 : 1, ...(showSend ? null : s.billBtnSolo) }}
              onClick={() => navigate('cart', { table, ticketId, lines, total })}
              disabled={!lines.length}
            >
              REQUEST BILL
            </button>
          </div>
        </div>

        {/* RIGHT — menu grid */}
        <div className="order-menu" style={s.menuPanel}>
          {/* Menu search */}
          <div style={s.searchRow}>
            <input
              style={s.searchInput}
              type="text"
              placeholder="🔍 Search menu..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>

          {isSearching ? (
            <p style={s.searchResultsLabel}>
              RESULTS FOR "{searchText.trim()}" ({filteredItems.length})
            </p>
          ) : (
            /* Category tabs */
            <div style={s.catTabs}>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  style={{
                    ...s.catTab,
                    background: activeCategory === cat.id ? C.orange : C.surface,
                    color:      activeCategory === cat.id ? '#fff' : C.muted,
                  }}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.name.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {/* Items grid */}
          <div className="menu-grid" style={s.itemsGrid}>
            {filteredItems.map(item => (
              <button key={item.id} style={s.itemTile} onClick={() => addItem(item)}>
                {item.image_url ? (
                  <img src={item.image_url} alt="" style={s.itemImage} />
                ) : (
                  <div style={s.itemIconFallback}>
                    <i
                      className={`ti ${iconForCategory(categoryNameById[item.category_id])}`}
                      style={s.itemIcon}
                      aria-hidden="true"
                    />
                  </div>
                )}
                <span style={s.itemName}>{item.name}</span>
                <span style={s.itemPrice}>KES {Number(item.price).toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  container:    { backgroundColor: C.bg, height: '100dvh', display: 'flex', flexDirection: 'column' },
  header:       { display:'flex', justifyContent:'space-between', alignItems:'center',
                   padding:'10px 16px', borderBottom:`1px solid var(--border)`, flexShrink:0 },
  loadErrorBanner: { padding:'6px 16px', background:'var(--warning)', color:'#1c1c1c',
                   fontSize:12, fontWeight:700, textAlign:'center', flexShrink:0 },
  backBtn:      { background:'none', border:'none', color:C.orange, fontSize:14, fontWeight:700,
                   cursor:'pointer', fontFamily:'inherit', letterSpacing:1 },
  tableTitle:   { color:C.text, fontSize:20, fontWeight:700, letterSpacing:2 },
  myOrdersBtn:  { background:'none', border:'none', color:C.muted, fontSize:12, cursor:'pointer', fontFamily:'inherit' },
  body:         { display:'flex', flex:1, overflow:'hidden' },
  ticketPanel:  { width:280, borderRight:`1px solid var(--border)`, display:'flex', flexDirection:'column', flexShrink:0 },
  ticketHeader: { display:'flex', justifyContent:'space-between', alignItems:'center',
                   padding:'10px 12px', borderBottom:`1px solid var(--border)` },
  ticketLabel:  { color:C.muted, fontSize:12, fontWeight:700, letterSpacing:2 },
  ticketTotal:  { color:C.orange, fontSize:18, fontWeight:700 },
  linesList:    { flex:1, overflowY:'auto', padding:'8px 0' },
  emptyMsg:     { color:C.muted, fontSize:13, textAlign:'center', marginTop:32, padding:'0 16px' },
  lineItem:     { display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                   borderBottom:`1px solid var(--border)` },
  lineName:     { flex:1, color:C.text, fontSize:13, fontWeight:600 },
  lineControls: { display:'flex', alignItems:'center', gap:6 },
  qtyBtn:       { background:'var(--surface-2)', border:'none', color:C.text, width:24, height:24,
                   borderRadius:4, cursor:'pointer', fontSize:16, fontFamily:'inherit' },
  lineQty:      { color:C.orange, fontSize:14, fontWeight:700, minWidth:20, textAlign:'center' },
  lineSubtotal: { color:C.muted, fontSize:12, minWidth:60, textAlign:'right' },
  ticketActions:{ padding:12, borderTop:`1px solid var(--border)`, display:'flex', flexDirection:'column', gap:8 },
  sendBtn:      { padding:'12px 0', background:C.orange, border:'none', borderRadius:8,
                   color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', letterSpacing:1 },
  billBtn:      { padding:'10px 0', background:'var(--table-bill)', border:'none', borderRadius:8,
                   color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', letterSpacing:1 },
  billBtnSolo:  { padding:'12px 0', fontSize:15 },
  menuPanel:    { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  searchRow:    { padding:'8px 8px 0', flexShrink:0 },
  searchInput:  { width:'100%', padding:'8px 10px', background:C.surface, border:`1px solid var(--border)`,
                   borderRadius:6, color:C.text, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' },
  searchResultsLabel: { color:C.muted, fontSize:11, fontWeight:700, letterSpacing:1,
                   padding:'8px 8px 0', flexShrink:0 },
  catTabs:      { display:'flex', gap:4, padding:'8px 8px 0', overflowX:'auto', flexShrink:0 },
  catTab:       { border:'none', borderRadius:'6px 6px 0 0', padding:'8px 14px', cursor:'pointer',
                   fontSize:12, fontWeight:700, letterSpacing:1, fontFamily:'inherit', whiteSpace:'nowrap' },
  itemsGrid:    { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'var(--gap-sm)', padding:8,
                   overflowY:'auto', flex:1 },
  itemTile:     { background:C.surface, border:'none', borderRadius:8, padding:'8px 6px',
                   cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center',
                   gap:4, fontFamily:'inherit', transition:'background 0.15s' },
  itemImage:      { width:'100%', height:64, objectFit:'cover', borderRadius:6 },
  itemIconFallback:{ width:'100%', height:64, background:'var(--surface-2)', borderRadius:6,
                   display:'flex', alignItems:'center', justifyContent:'center' },
  itemIcon:     { fontSize:26, color:C.muted },
  itemName:     { color:C.text, fontSize:12, fontWeight:600, textAlign:'center', lineHeight:1.2 },
  itemPrice:    { color:C.orange, fontSize:12, fontWeight:700 },
};
